import { EnvironmentProviders, inject, Injectable, Provider } from '@angular/core';
import {
  IFloatingPopupActions,
  IFloatingPopupConfig,
  IFloatingPopupGroupInstance,
} from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { FpCorrelationContentComponent } from '../components/content/fp-correlation-content.component';
import { FpCorrelationMinimizedContentComponent } from '../components/minimized-content/fp-correlation-minimized-content.component';
import { FpCorrelationTabs } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/enums/fp-correlation-tabs.enum';
import { FpCorrelationTabsLocalize } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/localize/fp-correlation-tabs';
import { IFloatingPopupEntityService } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-entity-service';
import { _, TranslateService } from '@ngx-translate/core';
import { fpCorrelationProvider } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/providers/fp-correlation.providers';
import { IFpCorrelationData } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/interfaces/fp-correlation-data';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { take } from 'rxjs/operators';
import { selectLoggerDeploymentEntities } from '@app/main/main-screen/store/data/logger-deployments/logger-deployments.selectors';
import { formatSerialNo } from '@app/shared/utils/string';
import { ITranslationSubscription } from '@app/core/interfaces/localization';

@Injectable({ providedIn: 'root' })
export class FpCorrelationService implements IFloatingPopupEntityService<ICorrelation> {
  protected popupService = inject(FloatingPopupService);
  private translateService = inject(TranslateService);
  private store = inject(Store);

  private floatingPopupGroup: IFloatingPopupGroupInstance = {
    id: 'correlation-popups',
    orderWeight: 1,
    minimizeTitle: (count: number) =>
      this.translateService.instant(_('@@floating-popup-group-correlation-name'), { I18N_VAR_COUNT: count }),
    tabs: {
      values: [
        FpCorrelationTabs.CorrelationGraph,
        FpCorrelationTabs.CorrelationSpectrum,
        FpCorrelationTabs.Summary,
        FpCorrelationTabs.Comments,
      ],
      localize: FpCorrelationTabsLocalize,
    },
  };

  private readonly instanceProviders: (Provider | EnvironmentProviders)[] = [fpCorrelationProvider()];

  public open(item: ICorrelation, actions?: IFloatingPopupActions) {
    this.store
      .select(selectLoggerDeploymentEntities)
      .pipe(take(1))
      .subscribe(loggerDeploymentsMap => {
        const loggerDeployment1 = loggerDeploymentsMap[item.logger1DeploymentId] || null;
        const loggerDeployment2 = loggerDeploymentsMap[item.logger2DeploymentId] || null;

        if (!loggerDeployment1 || !loggerDeployment2) {
          console.error('LoggerDeployments for Correlation not found', item);
          return;
        }

        const title = this.translateService.get(_('@@floating-popup-header-correlation-name'), {
          I18N_VAR_LOGGER_SERIAL_NO1: formatSerialNo(loggerDeployment1.serialNo),
          I18N_VAR_LOGGER_SERIAL_NO2: formatSerialNo(loggerDeployment2.serialNo),
        });

        const shortTitle: ITranslationSubscription = of(
          `${formatSerialNo(loggerDeployment1.serialNo)} - ${formatSerialNo(loggerDeployment2.serialNo)}`
        );

        const floatingPopupConfig: IFloatingPopupConfig = {
          component: FpCorrelationContentComponent,
          minimizedComponent: FpCorrelationMinimizedContentComponent,
          selectedTab: FpCorrelationTabs.CorrelationGraph,
          instanceProviders: this.instanceProviders,
          width: 840,
          height: 500,
          minWidth: 480,
          actions,
        };

        this.popupService.open<IFpCorrelationData>(
          { ...floatingPopupConfig, title, shortTitle },
          {
            correlation: { ...item },
            loggerDeployment1: { ...loggerDeployment1 },
            loggerDeployment2: { ...loggerDeployment2 },
          },
          this.floatingPopupGroup
        );
      });
  }
}
